import { useRef, useEffect, useState } from "react";
import Modal from "./Modal";
import useTranslation from "../../hooks/useTranslation";
import useFormValidation from "../cardView/useFormValidation";
import { Button } from "../generic/Core";
import styled from "styled-components";

//Sub Components
import GroupFormElem from "../cardView/GroupFormElem";
import FormElem from "../cardView/FormElem";
import { widgetFor } from "./widgets";
import useModalSubmit from "./useModalSubmit";

import { getNestedValue } from "../../utils/objects/nestedValue"

const Spacer = styled.div`
    height: 20px;
`

/**
 * A form in a modal.
 *
 * Since 33.1 tier 3 this is the only component that draws one. What used to be
 * three renderers is this file plus two declared things: which widgets the
 * entry lists, and where its `submit` sends the answer. A record-editing modal
 * is a `cardSection` widget with `submit: { to: 'entity' }`, and `ModalCard` is
 * now only the store and the load that such a modal needs.
 */
const ModalForm = ({
    isVisible,
    setIsVisible,
    modalTitle,
    id,
    groups,
    buttons,
    submit,
    submitPath,
    queryArray,
    recordId,
    validateOnSubmit = false,
    lineId,
    validated: validatedProp,
    section,
    mode = '',
    entity,
    useStore,
    path = [],
    testId,
    origin,
    opacity,
    minHeight,
    zIndex,
    options,
    onClose,
    onDone,
    parentId,
    onUpdate
}) => {
    const { t } = useTranslation()

    const modalPadding = section?.meta?.modalPadding || '1rem'

    // The modal's own chrome — its close button and its footer — is named by the
    // modal, not by whatever opened it. There are many triggers (line 3's pencil
    // is not line 4's, so a trigger's id has to be composed) but only ever one
    // modal open, so the modal needs no more identity than its own name.
    // `testId` is the fallback for a form declared inline, which has no id.
    const chromeId = id ?? testId

    // A modal form confirms with one button, so that is the default rather than
    // something all sixteen entries have to restate. A form declares `buttons`
    // when it wants different ones; `id` is the test id and `action` is what the
    // button does — `submit` writes and closes, `close` just closes.
    const footer = buttons ?? [{ id: 'save', label: 'all.form.button.update', action: 'submit' }]
    // Get state based on path
    const [state, setState] = useStore(s => path.length === 0 ? s : getNestedValue(s, path))

    // A card modal owns its own validation, because nothing above it is going to
    // mark the form submitted; a form modal inherits the card's.
    const { validated: entityValidated, setValidated } = useFormValidation(entity)
    const validated = validateOnSubmit ? entityValidated : validatedProp

    const focusRef = useRef(null)
    const updateBtnRef = useRef(null)
    const modalRef = useRef(null)
    const [rect, setRect] = useState(null)

    useEffect(() => {
        if (focusRef.current) { focusRef.current.focus() }
        if (modalRef.current) setRect(modalRef.current.getBoundingClientRect())
    }, [])

    // Handle closing the modal with optional callback
    const handleClose = async () => {
        setIsVisible(false)
        if (typeof onClose === 'function') {
            await onClose()
        }
    }

    const { submitForm, NotificationComponent } = useModalSubmit({
        submit,
        entity,
        path: submitPath,
        queryArray,
        parentId,
        recordId,
        state,
        setState,
        statePath: path,
        onDone,
    })

    // One submit at a time. A record modal POSTs, refetches and holds for two
    // seconds before it closes; with the button live meanwhile, each click on a
    // new record created another one. The ref answers a second click that lands
    // before the re-render that disables the button.
    const [submitting, setSubmitting] = useState(false)
    const submittingRef = useRef(false)

    const handleSubmit = async () => {
        if (submittingRef.current) return
        submittingRef.current = true
        setSubmitting(true)
        try {
            if (validateOnSubmit) setValidated(true)
            const result = await submitForm()
            // A rejected POST leaves the modal open with the edit in it. Only the
            // store destination closes first, because it cannot fail.
            if (result.ok) await handleClose()
        } finally {
            submittingRef.current = false
            setSubmitting(false)
        }
    }

    // Both footer actions have to swallow the event: these are plain <button>s,
    // and a modal that contains a lines widget contains a <form>, so letting the
    // default through submits it natively and reloads the page.
    const handleFooterClick = (button) => (e) => {
        e.preventDefault()
        if (button.action === 'close') return handleClose()
        return handleSubmit()
    }

    //Enter on the focused submit button confirms the form. Escape is the shell's
    //job — see the openModals note in Modal.jsx.
    const handleKeyDown = (e) => {
        if (e.key === 'Enter' && e.target === updateBtnRef.current) {
            updateBtnRef.current.click()
        }
    }

    // What a modal *inside* this one is opened from. A nested modal reads it to
    // drop its own backdrop and sit below this one rather than over it.
    const childOrigin = { nature: 'modal', rect }

    return (
        <Modal
            ref={modalRef}
            onClose={handleClose}
            show={isVisible}
            mode={mode}
            className={''}
            handleKeyDown={handleKeyDown}
            zIndex={zIndex}
            opacity={opacity ?? (origin?.nature === 'modal' ? 0 : 0.5)}
            minHeight={minHeight ?? `${origin?.rect?.height}px`}
            $margin={origin?.nature === 'modal' ? '96px 0 0 0' : 0}
        >
            <Modal.Header onClose={handleClose} testId={chromeId}>
                <h3 style={{ margin: '0px' }}>{t(modalTitle)}</h3>
            </Modal.Header>
            <Modal.Body $padding={modalPadding}>
                {NotificationComponent}
                {groups.map((item, j) => {
                    // A widget, a field row, or a field — in that order. The
                    // widget list is `./widgets`, not a chain of ternaries, so a
                    // modal can contain something this file has never heard of.
                    const Widget = widgetFor(item.type)
                    if (Widget) return (
                        <Widget
                            key={`group-${j}`}
                            item={item}
                            element={item}
                            section={item.section}
                            state={state}
                            entity={entity}
                            validated={validated}
                            useStore={useStore}
                            lineId={lineId}
                            path={path}
                            options={options}
                            origin={childOrigin}
                            parentId={parentId}
                            onUpdate={onUpdate}
                            onClose={handleClose}
                        />
                    )

                    if (item.type === 'group') return (
                        <GroupFormElem
                            key={`group-${j}`}
                            item={item}
                            lineId={lineId}
                            validated={validated}
                            useStore={useStore}
                            ref={item.focus ? focusRef : null}
                            section={section}
                            entity={entity}
                            path={path}
                        />
                    )

                    return (
                        <FormElem
                            key={`group-${j}`}
                            item={item}
                            validated={validated}
                            lineId={lineId}
                            ref={item.focus ? focusRef : null}
                            section={section}
                            entity={entity}
                            useStore={useStore}
                            path={path}
                        />
                    )
                })}
            </Modal.Body>
            <Modal.Footer>
                {footer.map((button, i) => (
                    <Button
                        key={button.id ?? i}
                        ref={button.action === 'submit' ? updateBtnRef : undefined}
                        $variant={button.variant ?? 'primary'}
                        onClick={handleFooterClick(button)}
                        disabled={submitting}
                        data-testid={`${chromeId}.${button.id}`}
                    >
                        {t(button.label)}
                    </Button>
                ))}
            </Modal.Footer>
            {(mode === 'fullscreen') && <Spacer />}
        </Modal>
    )
}

export default ModalForm
